import { useTranslation } from 'react-i18next';

import { LOCALES, type Locale } from './locales';
import { baseDomain } from './site';

export { LOCALE_NAMES, LOCALES, type Locale } from './locales';

/** Where an explicit language choice made on this device is kept. */
export const LOCALE_STORAGE_KEY = 'easymusic:lang';
/**
 * The same choice in a cookie of the whole installation: the static front page sets it
 * too (site/session.ts), the gateway sends the domain to that language's page, and a
 * host where nothing was chosen yet starts in it.
 */
export const LOCALE_COOKIE = 'easymusic_lang';

/**
 * Where the interface's language comes from, in order (app/i18n.ts): the address
 * (`?lang=ru`: what search engines link to, one address per language; docs/seo.md), a
 * choice made on this device, one made at another host or on the front page, the
 * browser. Only an explicit choice is stored (see rememberLocale): not what was detected,
 * so the address's language is for this visit.
 */
export const LANGUAGE_DETECTION = {
  order: ['querystring', 'localStorage', 'cookie', 'navigator'],
  lookupQuerystring: 'lang',
  lookupLocalStorage: LOCALE_STORAGE_KEY,
  lookupCookie: LOCALE_COOKIE,
  caches: [] as string[],
};

export function isLocale(value: string | null | undefined): value is Locale {
  return (LOCALES as readonly string[]).includes(value ?? '');
}

/** The language to request content in: the interface language, resolved to a supported one. */
export function useContentLocale(): Locale {
  const { i18n } = useTranslation();
  return isLocale(i18n.resolvedLanguage) ? i18n.resolvedLanguage : 'en';
}

/** Remember a language the learner picked themselves; it then wins over any other source. */
export function rememberLocale(locale: Locale): void {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Storage may be unavailable; the choice then lasts for this visit only.
  }
  const domain = baseDomain();
  document.cookie = `${LOCALE_COOKIE}=${locale}; Path=/; Max-Age=${365 * 86_400}; SameSite=Lax${domain ? `; Domain=${domain}` : ''}`;
}

/** Whether a language was chosen on this device: here, at another host, or on the front page. */
export function hasRememberedLocale(): boolean {
  const cookie = document.cookie.split('; ').find((pair) => pair.startsWith(`${LOCALE_COOKIE}=`));
  if (isLocale(cookie?.slice(LOCALE_COOKIE.length + 1))) return true;
  try {
    return isLocale(localStorage.getItem(LOCALE_STORAGE_KEY));
  } catch {
    return false;
  }
}
